import React, { useState } from "react";
import { compose } from "redux";
import Select from "react-select";
import FA from "../../containers/fa";
import withAPI from "../../services/api";

// Tri-state per-class override of lesson visibility:
//   null  = Use default (inherit school/global setting)
//   true  = All Lessons (full library)
//   false = Recommended Only
// The effective value a learner sees is resolved server-side (class -> org ->
// global default), so this control only records the class-level override.

// Global fallback used when neither the class nor the org expresses a stance.
const GLOBAL_DEFAULT = false; // Recommended Only

const labelForResolved = (v) => (v ? "All Lessons" : "Recommended Only");

// What "Use default" resolves to for this class: the org's setting if it has
// one, otherwise the global default.
const resolveDefault = (orgValue) =>
  orgValue === true || orgValue === false ? orgValue : GLOBAL_DEFAULT;

const keyForValue = (v) =>
  v === true ? "true" : v === false ? "false" : "unset";

const FullLessonViewModal = ({
  api,
  clsId,
  initialValue,
  orgValue,
  onClose,
  onSaved,
}) => {
  const [value, setValue] = useState(
    initialValue === undefined ? null : initialValue
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const resolvedDefault = resolveDefault(orgValue);
  const defaultSource =
    orgValue === true || orgValue === false ? "school" : "global";
  const options = [
    {
      key: "unset",
      label: `Use default — ${defaultSource} setting (${labelForResolved(
        resolvedDefault
      )})`,
      value: null,
    },
    { key: "true", label: "All Lessons", value: true },
    { key: "false", label: "Recommended Only", value: false },
  ];

  const selectedOption = options.find((o) => o.key === keyForValue(value));

  const selectStyles = {
    menuList: (base) => ({
      ...base,
      maxHeight: "30vh",
      "::-webkit-scrollbar": {
        width: "8px",
      },
      "::-webkit-scrollbar-track": {
        background: "#dedede",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#676767",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#676767",
      },
    }),
  };

  const handleSelect = (option) => {
    setValue(option.value);
  };

  const handleSave = () => {
    setSaving(true);
    setError(null);
    api
      .patchUserTagInfo(clsId, { enable_full_lesson_view: value })
      .then(() => {
        if (onSaved) {
          onSaved(clsId, value);
        }
        setSaving(false);
        onClose();
      })
      .catch((e) => {
        console.error("Failed to update full lesson view:", e);
        setError("Something went wrong. Please try again.");
        setSaving(false);
      });
  };

  return (
    <div className='pdf-upload'>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxWidth: "565px",
          zIndex: "1050",
        }}
      >
        <div className='common_border'>
          <div className='common_heading'>
            <p>Lesson View</p>
            <button
              autoFocus
              className='pdf_popup_close'
              onClick={onClose}
              disabled={saving}
            >
              <FA icon='times' />
            </button>
          </div>
          <div className='common_dashboard_bg'>
            <p>
              When a Learner is selecting what Lesson to take next, should they
              be able to see all available Lessons, or a set of Lessons
              recommended for them based on their grade and progress? Leave this
              on the default to follow your school's (or the global) setting.
            </p>

            {error && <div className='alert alert-danger'>{error}</div>}

            <div style={{ margin: "20px 0" }}>
              <Select
                options={options}
                value={selectedOption}
                onChange={handleSelect}
                isDisabled={saving}
                isSearchable={false}
                aria-label='Lesson View setting'
                styles={selectStyles}
                menuPlacement='auto'
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button
                className='btn btn-md btn-warning'
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className='btn btn-md btn-primary'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default compose(withAPI)(FullLessonViewModal);
